import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import Confetti from "../Confetti";
import { Calendar, MapPin, Check, Coffee, Loader2, Trash2, Pencil } from "lucide-react";

// Energy/mood options
const ENERGY_OPTIONS = [
  { id: "focused", name: "Focused", emoji: "🎯", color: "bg-blue-500" },
  { id: "social", name: "Social", emoji: "🎉", color: "bg-green-500" },
  { id: "quiet", name: "Quiet", emoji: "🌙", color: "bg-purple-500" },
  { id: "tired", name: "Tired", emoji: "😴", color: "bg-orange-500" },
];

// Dog profiles
const DOGS = [
  {
    id: "lluna",
    name: "Lluna",
    title: "Professional Yoga Assistant",
    bio: "Will join every meditation. Expects belly rubs as payment.",
    image: "https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/8quno680_LLuna.jpg",
  },
  {
    id: "jenny",
    name: "Jenny",
    title: "Known Donut Thief",
    bio: "Has never met a snack she didn't like. Expert at sad eyes.",
    image: "https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/n0c0tva9_Jenny.jpg",
  },
  {
    id: "viki",
    name: "Viki",
    title: "Chief Happiness Officer",
    bio: "Big ears, bigger heart. Will greet you at the door every time.",
    image: "https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/tdatr56p_Viki.jpg",
  },
];

const FeedTab = ({ user, headers, energyStats, todayEnergy, espressoTalks, upcomingArrivals, onRefresh }) => {
  const [localEnergy, setLocalEnergy] = useState(null);
  const currentEnergy = localEnergy || todayEnergy;
  const [submittingEnergy, setSubmittingEnergy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, talkId: null, topic: '' });
  const [showTalkForm, setShowTalkForm] = useState(false);
  // When editing your own talk, this holds the id (and the form is reused for both create + edit)
  const [editingTalkId, setEditingTalkId] = useState(null);
  const [newTalk, setNewTalk] = useState({
    topic: "",
    description: "",
    date: "",
    time: "10:00",
    location: "Living Room",
    duration_minutes: 20
  });
  const [submittingTalk, setSubmittingTalk] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const submitEnergy = async (energy) => {
    setSubmittingEnergy(true);
    try {
      await axios.post(`${API}/community/energy`, { energy }, { headers });
      setLocalEnergy(energy);
      toast.success("Thanks for checking in! 🙌");
      onRefresh();
    } catch (error) {
      toast.error("Couldn't save your check-in");
    } finally {
      setSubmittingEnergy(false);
    }
  };

  const startEditingTalk = (talk) => {
    setEditingTalkId(talk.id);
    setNewTalk({
      topic: talk.topic || "",
      description: talk.description || "",
      date: talk.date || "",
      time: talk.time || "10:00",
      location: talk.location || "Living Room",
      duration_minutes: talk.duration_minutes || 20,
    });
    setShowTalkForm(true);
    // Smooth scroll to the form near the top of the espresso section
    setTimeout(() => {
      document.querySelector('[data-testid="espresso-talk-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const cancelTalkEdit = () => {
    setEditingTalkId(null);
    setShowTalkForm(false);
    setNewTalk({ topic: "", description: "", date: "", time: "10:00", location: "Living Room", duration_minutes: 20 });
  };

  const submitEspressoTalk = async () => {
    if (!newTalk.topic || !newTalk.date) {
      toast.error("Please fill in the topic and date");
      return;
    }
    setSubmittingTalk(true);
    try {
      if (editingTalkId) {
        await axios.put(`${API}/community/espresso-talks/${editingTalkId}`, newTalk, { headers });
        toast.success("Talk updated ☕");
      } else {
        await axios.post(`${API}/community/espresso-talks`, newTalk, { headers });
        toast.success("Espresso Talk scheduled! ☕");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 100);
      }
      setShowTalkForm(false);
      setEditingTalkId(null);
      setNewTalk({ topic: "", description: "", date: "", time: "10:00", location: "Living Room", duration_minutes: 20 });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingTalkId ? "Couldn't update talk" : "Couldn't schedule talk"));
    } finally {
      setSubmittingTalk(false);
    }
  };

  const attendTalk = async (talkId) => {
    try {
      await axios.post(`${API}/community/espresso-talks/${talkId}/attend`, {}, { headers });
      toast.success("You're in! See you there ☕");
      onRefresh();
    } catch (error) {
      toast.error("Couldn't sign up");
    }
  };

  const cancelAttendance = async (talkId) => {
    try {
      await axios.delete(`${API}/community/espresso-talks/${talkId}/attend`, { headers });
      toast.success("Cancelled your attendance");
      onRefresh();
    } catch (error) {
      toast.error("Couldn't cancel");
    }
  };

  const deleteTalk = async (talkId) => {
    try {
      await axios.delete(`${API}/community/espresso-talks/${talkId}`, { headers });
      toast.success("Talk cancelled");
      setDeleteConfirm({ show: false, talkId: null, topic: '' });
      onRefresh();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Couldn't delete talk");
    }
  };

  const confirmDeleteTalk = (talk) => {
    setDeleteConfirm({ show: true, talkId: talk.id, topic: talk.topic });
  };

  const isAttending = (talk) => {
    return talk.attendees?.some(a => a.user_id === user?.id);
  };

  const formatTalkDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <>
    <div className="space-y-6">
      {/* Daily Energy Check-in */}
      <div className="bg-gradient-to-br from-[#F0EDE3] to-[#E8E4D0] rounded-xl p-6">
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">How are you feeling today?</h3>
        <p className="text-[#1A1A1A]/60 text-sm mb-4">Help us understand the house vibe</p>
        
        {currentEnergy ? (
          <div className="flex items-center gap-3 bg-white rounded-2xl p-4">
            <span className="text-3xl">{ENERGY_OPTIONS.find(e => e.id === currentEnergy)?.emoji}</span>
            <div>
              <p className="font-medium text-[#1A1A1A]">You're feeling {currentEnergy}</p>
              <p className="text-xs text-[#1A1A1A]/50">Check in again tomorrow!</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {ENERGY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => submitEnergy(option.id)}
                disabled={submittingEnergy}
                className="bg-white rounded-2xl p-4 text-center hover:scale-105 transition-transform"
              >
                <span className="text-2xl block mb-1">{option.emoji}</span>
                <span className="text-xs text-[#1A1A1A]">{option.name}</span>
              </button>
            ))}
          </div>
        )}

        {energyStats && (
          <div className="mt-4 pt-4 border-t border-[#1A1A1A]/10">
            <p className="text-xs text-[#1A1A1A]/60 mb-2">Today's house vibe:</p>
            <div className="flex gap-2">
              {Object.entries(energyStats.distribution || {}).map(([energy, count]) => (
                <div key={energy} className="flex items-center gap-1 bg-white/50 rounded-full px-3 py-1">
                  <span>{ENERGY_OPTIONS.find(e => e.id === energy)?.emoji}</span>
                  <span className="text-xs font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Espresso Talks ☕✨ */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 sm:p-7 border border-amber-100">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-xl text-[#1A1A1A]">Espresso Talks</h3>
            <p className="text-sm text-[#1A1A1A]/60 mt-1.5 leading-relaxed">
              Casual 20-minute sharing sessions. No stress, just open minds and hearts.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              if (showTalkForm) {
                cancelTalkEdit();
              } else {
                setShowTalkForm(true);
              }
            }}
            className="bg-[#C85A38] hover:bg-[#B85540] text-white rounded-full text-xs px-4 self-start sm:self-auto flex-shrink-0"
          >
            {showTalkForm ? "Cancel" : "+ Host a Talk"}
          </Button>
        </div>

        {/* New Talk Form (also used for editing your own talk) */}
        {showTalkForm && (
          <div className="bg-white rounded-2xl p-4 mb-4 space-y-3" data-testid="espresso-talk-form">
            {editingTalkId && (
              <p className="text-xs text-[#C85A38] font-medium">Editing your talk</p>
            )}
            <input
              type="text"
              placeholder="What will you talk about?"
              value={newTalk.topic}
              onChange={(e) => setNewTalk({ ...newTalk, topic: e.target.value })}
              className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
            />
            <textarea
              placeholder="Brief description (optional)"
              value={newTalk.description}
              onChange={(e) => setNewTalk({ ...newTalk, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30 resize-none"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="date"
                value={newTalk.date}
                onChange={async (e) => {
                  const newDate = e.target.value;
                  setNewTalk({ ...newTalk, date: newDate });
                  if (newDate && newTalk.time) {
                    try {
                      const res = await axios.get(`${API}/community/check-overlap?date=${newDate}&time=${newTalk.time}`, { headers });
                      setOverlapWarning(res.data.has_overlap ? res.data.overlapping_events : null);
                    } catch (e) { setOverlapWarning(null); }
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
              />
              <input
                type="time"
                value={newTalk.time}
                onChange={async (e) => {
                  const newTime = e.target.value;
                  setNewTalk({ ...newTalk, time: newTime });
                  if (newTalk.date && newTime) {
                    try {
                      const res = await axios.get(`${API}/community/check-overlap?date=${newTalk.date}&time=${newTime}`, { headers });
                      setOverlapWarning(res.data.has_overlap ? res.data.overlapping_events : null);
                    } catch (e) { setOverlapWarning(null); }
                  }
                }}
                className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
              />
              <select
                value={newTalk.location}
                onChange={(e) => setNewTalk({ ...newTalk, location: e.target.value })}
                className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
              >
                <option value="Living Room">Living Room</option>
                <option value="Dining Room">Dining Room</option>
                <option value="Pool Area">Pool Area</option>
                <option value="Kitchen">Kitchen</option>
                <option value="Garden">Garden</option>
                <option value="Terrace">Terrace</option>
                <option value="Driveway">Driveway</option>
              </select>
            </div>
            
            {overlapWarning && (
              <div className="bg-amber-100 border border-amber-300 rounded-xl p-3 text-sm">
                <p className="font-medium text-amber-800">⚠️ Heads up!</p>
                <p className="text-amber-700 text-xs mt-1">
                  There's already something scheduled around this time:
                  {overlapWarning.map((e, i) => (
                    <span key={i} className="block">• {e.name} at {e.time} ({e.location})</span>
                  ))}
                </p>
              </div>
            )}
            
            <Button
              onClick={submitEspressoTalk}
              disabled={submittingTalk}
              className="w-full bg-[#C85A38] hover:bg-[#B85540] text-white"
            >
              {submittingTalk ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingTalkId ? "Save changes" : "Schedule Talk")}
            </Button>
          </div>
        )}

        {/* Upcoming Talks */}
        {espressoTalks.length > 0 ? (
          <div className="space-y-4">
            {espressoTalks.map((talk) => (
              <div key={talk.id} className="bg-white rounded-2xl p-5 shadow-sm">
                {/* Header: avatar + topic + host */}
                <div className="flex items-start gap-3.5 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] flex items-center justify-center text-white font-serif overflow-hidden flex-shrink-0">
                    {talk.host_avatar ? (
                      <img src={talk.host_avatar} alt={talk.host_name} className="w-full h-full object-cover" />
                    ) : (
                      talk.host_name?.charAt(0)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A1A1A] text-base leading-tight">{talk.topic}</p>
                    <p className="text-xs text-[#1A1A1A]/60 mt-1">Hosted by {talk.host_name}</p>
                  </div>
                </div>

                {/* Description */}
                {talk.description && (
                  <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mb-3">{talk.description}</p>
                )}

                {/* Meta row: date + location */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#1A1A1A]/60 mb-3">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatTalkDate(talk.date)} at {talk.time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {talk.location}
                  </span>
                </div>

                {/* Attendees */}
                {talk.attendees && talk.attendees.length > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex -space-x-2">
                      {talk.attendees.slice(0, 4).map((attendee, i) => (
                        <div key={i} className="w-6 h-6 rounded-full bg-[#8C9A7C] flex items-center justify-center text-white text-xs border-2 border-white overflow-hidden">
                          {attendee.avatar ? (
                            <img src={attendee.avatar} alt={attendee.name} className="w-full h-full object-cover" />
                          ) : (
                            attendee.name?.charAt(0)
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-[#1A1A1A]/60">
                      {talk.attendees.length} attending
                    </span>
                  </div>
                )}

                {/* Action row: RSVP + edit/delete */}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-stone-100">
                  {isAttending(talk) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cancelAttendance(talk.id)}
                      className="text-xs rounded-full border-[#8C9A7C] text-[#8C9A7C] hover:bg-[#8C9A7C]/10"
                    >
                      <Check className="w-3 h-3 mr-1" /> Going
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => attendTalk(talk.id)}
                      className="text-xs rounded-full bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white"
                    >
                      Count me in!
                    </Button>
                  )}
                  {/* Edit + Delete buttons for host or admin */}
                  {(talk.host_id === user?.id || user?.is_admin === true || user?.role === "admin") && (
                    <div className="flex items-center gap-1 ml-auto">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditingTalk(talk)}
                        className="text-xs text-[#1A1A1A]/70 hover:text-[#1A1A1A] hover:bg-stone-100 rounded-full"
                        data-testid={`edit-talk-${talk.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => confirmDeleteTalk(talk)}
                        className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                        data-testid={`delete-talk-${talk.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white/50 rounded-2xl">
            <Coffee className="w-9 h-9 text-[#1A1A1A]/30 mx-auto mb-3" />
            <p className="text-sm text-[#1A1A1A]/60">No talks scheduled yet</p>
            <p className="text-xs text-[#1A1A1A]/40 mt-1">Be the first to host one!</p>
          </div>
        )}
      </div>

      {/* Upcoming Arrivals */}
      {upcomingArrivals.length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-stone-100 mt-6">
          <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Arriving soon</h3>
          <div className="space-y-3">
            {upcomingArrivals.map((guest, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-[#F0EDE3]/50 rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-[#8C9A7C] flex items-center justify-center text-white font-serif">
                  {guest.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#1A1A1A] text-sm">{guest.name}</p>
                  <p className="text-xs text-[#1A1A1A]/60">{guest.nationality} • {guest.occupation}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-[#C85A38]">{guest.arriving_in}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dogs Section */}
      <div className="bg-gradient-to-br from-[#8C9A7C]/20 to-[#F0EDE3] rounded-xl p-5">
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Meet the Savi dogs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {DOGS.map((dog) => (
            <div key={dog.id} className="bg-white rounded-2xl p-4 flex flex-row sm:flex-col items-center sm:text-center gap-4 sm:gap-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 sm:mx-auto sm:mb-2 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src={dog.image} 
                  alt={dog.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 sm:flex-none">
                <p className="font-medium text-[#1A1A1A]">{dog.name}</p>
                <p className="text-xs text-[#C85A38] italic">{dog.title}</p>
                <p className="text-xs text-[#1A1A1A]/60 mt-1 sm:mt-2">{dog.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

      {/* Confetti Celebration */}
      <Confetti trigger={showConfetti} />

      {/* Delete Confirmation Modal */}
    {deleteConfirm.show && (
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">Cancel Espresso Talk?</h3>
            <p className="text-sm text-[#1A1A1A]/60 mb-6">
              Are you sure you want to cancel "<strong>{deleteConfirm.topic}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setDeleteConfirm({ show: false, talkId: null, topic: '' })}
              >
                Keep It
              </Button>
              <Button
                className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 text-white"
                onClick={() => deleteTalk(deleteConfirm.talkId)}
              >
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FeedTab;
